import React, { FC, useMemo, useState, useEffect } from 'react';
import * as propz from 'propz';
import { ScreenDataModel, VIEW_TYPE } from 'types/tv';
import FlagIcon from '@mui/icons-material/Flag';
import { Tournament } from 'models/tournament';
import { School } from 'models/school';
import { Record as RecordModel } from 'models/record';
import TVIndividualResultsTable from './TVIndividualResultsTable';
import SquadInTouchLogo from '../SquadInTouchLogo/SquadInTouchLogo';
import TVOverallByAgeGroup from './TVOverallByAgeGroup';
import { TVOverallJuniorByYear } from './TVOverallJuniorByYear';
import { TVOverallSeniorByYear } from './TVOverallSeniorByYear';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material';
import { getClubGenderConvertedFromSportGender } from 'helpers/score';
import { convertPointsToStringWithoutDelimeter } from 'helpers/eventView';

interface Props {
  data: ScreenDataModel;
  tournament: Tournament;
  unionSchool?: School;
  allRecords: RecordModel[];
}

const TVGroupScreen: FC<Props> = ({
  data,
  tournament,
  unionSchool,
  allRecords,
}) => {
  const activeSchoolId = tournament.schoolId as string;
  const singleEvent = data.viewType === VIEW_TYPE.SINGLE_EVENT ? data.events[0] : null;

  const displayEventName = useMemo(() => {
    if (!singleEvent) return '';
    const byUnion = propz.get(singleEvent, ['generatedNames', activeSchoolId], '');
    if (byUnion) return byUnion;
    return propz.get(singleEvent, ['generatedNames', 'official'], '');
  }, [singleEvent, activeSchoolId]);

  const priorRecordStr = useMemo(() => {
    if (!singleEvent) return '';

    const eventTimestamp = new Date(
      propz.get(singleEvent, ['startTime'], '')
    ).getTime();
    const eventAges: number[] = propz.get(singleEvent, ['ages'], []);
    const eventMinAge = Math.min(...eventAges);
    const eventMaxAge = Math.max(...eventAges);

    const matching: RecordModel[] = (allRecords || []).filter((rec) => {
      const recAges: number[] = propz.get(rec, ['ages'], []);
      const recMinAge = Math.min(...recAges);
      const recMaxAge = Math.max(...recAges);
      const sameAgeGroup = recMinAge === eventMinAge && recMaxAge === eventMaxAge;

      const recGenderRaw = (propz.get(rec, ['gender'], '') as string) || '';
      const recGenderConv = getClubGenderConvertedFromSportGender(recGenderRaw);
      const sameGender =
        recGenderConv === singleEvent.gender ||
        recGenderRaw + '_ONLY' === singleEvent.gender;

      const sameSport =
        propz.get(rec, ['sportId'], '') ===
        propz.get(singleEvent, ['sport', 'id'], '');

      const recDate = new Date(propz.get(rec, ['date'], '')).getTime();
      const earlierThanEvent = recDate < eventTimestamp;

      return sameAgeGroup && sameGender && sameSport && earlierThanEvent;
    });

    let chosen: RecordModel | undefined;
    if (matching.length > 0) {
      chosen = matching.reduce((closest, candidate) => {
        const candTime = new Date(propz.get(candidate, ['date'], '')).getTime();
        const closeTime = new Date(propz.get(closest, ['date'], '')).getTime();
        return Math.abs(eventTimestamp - candTime) <
          Math.abs(eventTimestamp - closeTime)
          ? candidate
          : closest;
      }, matching[0]);
    }

    if (chosen && chosen.record != null) {
      const raw = chosen.record;
      const numericValue = typeof raw === 'string' ? Number(raw) : (raw as number);
      return convertPointsToStringWithoutDelimeter(singleEvent, numericValue);
    }

    return '';
  }, [singleEvent, allRecords]);

  const participantsData = data.participantsData ?? [];
  const { pageIndex = 0, pageCount = 1 } = participantsData[0] ?? {};

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          backgroundColor: 'rgba(255,255,255,0.85)',  
          display: 'flex',
          alignItems: 'center',
          px: 2,
          justifyContent: 'center',
          height: 70,
          borderBottom: 1,
          borderColor: 'grey.300',
          flexShrink: 0,
        }}
      >
      <Box
        sx={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 64,
          width: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {unionSchool?.pic ? (
          <img
            src={unionSchool.pic}
            alt={`${unionSchool.name} Logo`}
            style={{ height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box
            sx={{
              width: 64,
              height: 64,
              backgroundColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No Logo
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            lineHeight: 1.2,
            mt: 1,     
            mb: 1,                  
            background: (theme: Theme) =>                
              `linear-gradient(
              90deg,
              ${theme.palette.primary.light},
              ${theme.palette.primary.main},
              ${theme.palette.primary.light}
              )`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: (theme: Theme) =>
            `0 0 6px ${theme.palette.primary.main}50`, 
          }}
        >
        {tournament.name}
      </Typography>
      <Box
        sx={{
          width: '120%',       
          height: 2,           
          my: 0.5, 
          mt: 0,
          mb: 1,             
          backgroundImage: (theme: Theme) =>
            `linear-gradient(
              90deg,
              transparent 0%,
              transparent 10%,
              ${theme.palette.primary.main} 50%,
              transparent 90%,
              transparent 100%
            )`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '100% 2px',
        }}
      />

        {singleEvent && (
          <Box sx={{ mt: 0, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 300,
                fontSize: '1.5rem',
                color: 'primary.main',
                mt: -1,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
            {displayEventName}
            </Typography>
            {priorRecordStr && (
              <Box 
                component="span" 
                sx={{ 
                  ml: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: '1rem',
                  fontWeight: 'normal',
                }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 300,
                    fontSize: '1.5rem',
                    color: 'primary.main',
                    mt: -1,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  >
                  (
                <FlagIcon sx={{ color: 'red', fontSize: '1rem', mr: 0.5 }} />
                  {priorRecordStr}
                  )
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {!singleEvent && data.viewType === VIEW_TYPE.GROUP_TABLE && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 300,
              fontSize: '1.5rem',
              color: 'primary.main',
              mt: -1,
            }}
          >
            {data.groupWithAge}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 40,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <SquadInTouchLogo />
      </Box>
    </Box>

    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        pt: 3,
        width: '100%',
      }}
    >
    {data.viewType === VIEW_TYPE.SINGLE_EVENT && singleEvent && (
      <Box
        sx={{
          width: '100%',
          px: 4,
          py: 2,
        }}
      >
        {(() => {
          const pd = data.participantsData?.[0] ?? { pageIndex: 0, pageCount: 1 };
          return (
            <TVIndividualResultsTable
              key={`ind-${singleEvent.id}-${pd.pageIndex}`}
              event={singleEvent}
              activeSchoolId={activeSchoolId}
              allRecords={allRecords}
              pageIndex={pd.pageIndex}
              pageCount={pd.pageCount}
            />
          );
        })()}
      </Box>
    )}


    {data.viewType === VIEW_TYPE.GROUP_TABLE && (
      <>
        {data.groupWithAge === 'Overall results by Age Group' && (
          <Box sx={{ 
            width: '100%', 
            px: 4, 
            py: 2 }}
            >
            <TVOverallByAgeGroup
              events={data.events}
              tournament={tournament}
              pageIndex={pageIndex}
              pageCount={pageCount}         
            />
          </Box>
        )}

        {data.groupWithAge === 'Overall Junior Results' && (
          <Box sx={{ 
            width: '100%', 
            px: 4, 
            py: 2 }}
            >
            <TVOverallJuniorByYear
              events={data.events}
              tournament={tournament}
              pageIndex={pageIndex}
              pageCount={pageCount}                 />
          </Box>
        )}

        {data.groupWithAge === 'Overall Senior Results' && (
          <Box sx={{ 
            width: '100%', 
            px: 4, 
            py: 2 }}
            >
            <TVOverallSeniorByYear
              events={data.events}
              tournament={tournament}
              pageIndex={pageIndex}
              pageCount={pageCount}                 />
          </Box>
        )}
      </>
    )}

    {data.viewType !== VIEW_TYPE.SINGLE_EVENT && data.viewType !== VIEW_TYPE.GROUP_TABLE && (
      <Box sx={{ mt: 4 }}>
        <Typography 
          sx={{ 
            color: 'text.secondary' 
          }}>
          Loading slide…
        </Typography>
      </Box>
    )}
      </Box>
    </Box>
  );
};

export default TVGroupScreen;
